// @flow

import { useCallback, useState } from 'react';

import { X } from 'lucide-react';

import { useDispatch } from 'react-redux';

import {
  NOTIFICATION_ID as CART_NOTIFICATION_ID,
  selectDomainFromFqdn,
  getCart,
  removeCartProduct,
} from '@gandi/cart';
import { notifyDanger, closeNotification } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Button, IconButton } from '@gandi/ui';

import PriceText from 'src/DomainCreate/components/DomainList/AvailabilityCell/PriceText';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { useCurrency } from 'src/hooks/useCurrency';

import { selectCatalogPriceByFqdnAndProcess } from '../modules/cart';
import type { DomainProduct } from '../types';

import { addToCart } from './modules/actions';

type Props = {
  result: DomainProduct,
  addToCartButtonLabel: string,
  process: 'create' | 'transfer',
};

export function PriceAndCartButton({
  result,
  addToCartButtonLabel,
  process,
}: Props): React.MixedElement | void {
  const { __ } = useTranslator();
  const dispatch = useDispatch();
  const { fqdn } = result;

  const resultInCart = useAppSelector(state =>
    selectDomainFromFqdn(state, fqdn),
  );

  const priceCatalog = useAppSelector(state =>
    selectCatalogPriceByFqdnAndProcess(state, { fqdn, process }),
  );

  const renewalPriceCatalog = useAppSelector(state =>
    selectCatalogPriceByFqdnAndProcess(state, { fqdn, process: 'renew' }),
  );

  const currency = useCurrency();

  const [isWorking, setIsWorking] = useState(false);

  const handleRemoveProduct = useCallback(async () => {
    if (!resultInCart?.id) return;
    setIsWorking(true);

    try {
      await dispatch(removeCartProduct(resultInCart.id));
    } catch (err) {
      notifyDanger(
        err._error || __('Unable to remove the product from the cart.'),
      );
    }

    await dispatch(getCart());
    setIsWorking(false);
  }, [__, dispatch, resultInCart?.id]);

  const handleAddToCart = useCallback(async () => {
    setIsWorking(true);

    try {
      await dispatch(addToCart({ domain: result, process }));
    } catch (err) {
      // $FlowFixMe closeNotification has a type issue
      dispatch(closeNotification(CART_NOTIFICATION_ID));
      dispatch(notifyDanger(__('An error occurred with your product.')));
    }

    await dispatch(getCart());
    setIsWorking(false);
  }, [__, dispatch, process, result]);

  return (
    <>
      {priceCatalog && currency && (
        <PriceText
          price={priceCatalog.price_before_taxes}
          renewPrice={renewalPriceCatalog?.price_before_taxes}
          currency={currency}
          minDuration={priceCatalog.min_duration}
          renewMinDuration={renewalPriceCatalog?.min_duration}
          needsPhaseSelection={false} // maybe create domain will need it
          priceDiff={
            priceCatalog.price_before_taxes -
            (renewalPriceCatalog?.price_before_taxes || 0)
          }
          durationChange={priceCatalog.options.duration_change}
        />
      )}
      {resultInCart?.id ? (
        <IconButton
          title={__('Remove')}
          kind="outline"
          small
          onClick={handleRemoveProduct}
          loading={isWorking}
        >
          <X />
        </IconButton>
      ) : (
        <Button onClick={handleAddToCart} loading={isWorking}>
          {addToCartButtonLabel}
        </Button>
      )}
    </>
  );
}
